"use client";

import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMe } from "@/components/Layout";
import { PageHeader } from "@/components/ui/PageHeader";
import { getModuleIconPath } from "@/lib/moduleIcons";
import { useTranslation } from "react-i18next";  // R29_FINAL_I18N


// ════════════════════════════════════════════════════════════════════════════
// Catch-all pra módulos cadastrados que ainda não têm página customizada.
// Quando o slug NÃO está em me.modulos → mostra 404.
// Quando ESTÁ → mostra placeholder "Em construção" com info do módulo.
//
// Pra ter páginas customizadas, criar:
//   frontend/pages/{slug}/index.tsx     (sobrescreve este catch-all)
// ou
//   frontend/pages/{slug}/sub.tsx       (sub-páginas funcionam normalmente)
// ════════════════════════════════════════════════════════════════════════════

export default function ModuloDinamicoPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const me = useMe();
  const slug = (router.query.modulo as string) ?? "";

  // Aguarda hidratação
  if (!router.isReady || !me) return null;

  const modulo = me.modulos.find(m => m.slug === slug);

  // Slug não é módulo do user → mostra 404
  if (!modulo) {
    return (
      <>
        <Head><title>{t("common.pageNotFound")}</title></Head>
        <div className="max-w-md mx-auto text-center py-16">
          <div className="text-6xl font-bold text-app-3 mb-4">404</div>
          <p className="text-app-2 mb-6">{t("common.pageNotFoundDesc")}</p>
          <Link href="/app" className="inline-flex items-center px-4 py-2 rounded-xl bg-app-1-inverted text-sm font-medium" style={{ background: "var(--text-1)", color: "var(--surface)" }}>
            {t("common.backToHome")}
          </Link>
        </div>
      </>
    );
  }

  // Módulo existe mas sem página customizada → placeholder
  return (
    <>
      <Head><title>{modulo.nome} · Platform</title></Head>
      <PageHeader
        title={modulo.nome}
        subtitle={modulo.descricao ?? t("moduloPage.subtitleDefault")}
      />

      <div className="rounded-2xl bg-app-surface border border-app p-10 text-center">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-app-soft border border-app flex items-center justify-center mb-5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10 text-app-2">
            <path strokeLinecap="round" strokeLinejoin="round" d={getModuleIconPath(modulo.icone)} />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-app-1 mb-2">{modulo.nome}</h2>
        <p className="text-app-3 text-sm max-w-md mx-auto mb-6">
          {t("moduloPage.placeholderBody")}
        </p>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-300">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t("moduloPage.badge")}
        </div>

        <p className="text-[10px] text-app-3 font-mono mt-6 opacity-60">slug: {modulo.slug}</p>
      </div>
    </>
  );
}
